import { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCompress, faUpRightAndDownLeftFromCenter } from '@fortawesome/free-solid-svg-icons';
import arrow from '../../../images/Arrow.svg';

const Minimap = ({ allNodesData, currentNodeId, viewerYaw, panorama_node, isLoadingData, virtualTourPlugin }) => {
	const minimapContainer = useRef(null);
	const minimap = useRef(null);
	const [isMinimapEnlarged, setIsMinimapEnlarged] = useState(false);

	// Initialize minimap - only once
	useEffect(() => {
		if (!minimapContainer.current || minimap.current) return;

		// Initialize minimap
		minimap.current = new maplibregl.Map({
			container: minimapContainer.current,
			center: [114.265542389, 22.336988960], // Default HKUST center
			zoom: 19.5,
			interactive: true,
			attributionControl: false,
			pitchWithRotate: false,
			dragRotate: false,
			pitch: 0,
			style: 'https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json',
			maxBounds: [
				[114.257125265, 22.331460829],
				[114.273959512, 22.340517091]
			]
		});

		// Helper function to populate minimap with data
		const populateMinimapData = () => {
			if (!minimap.current || !allNodesData || !panorama_node) return;

			const buildingGeojson = allNodesData.geojson || { type: 'FeatureCollection', features: [] };

			const nodesGeojson = {
				type: 'FeatureCollection',
				features: allNodesData.nodes.map(node => ({
					type: 'Feature',
					geometry: {
						type: 'Point',
						coordinates: [parseFloat(node.longitude), parseFloat(node.latitude)]
					},
					properties: {
						_id: node._id,
						name: node.name
					}
				}))
			};

			const edgesGeojson = {
				type: 'FeatureCollection',
				features: allNodesData.edges.map(edge => ({
					type: 'Feature',
					geometry: {
						type: 'LineString',
						coordinates: [
							[parseFloat(edge.from_panorama_node_longitude), parseFloat(edge.from_panorama_node_latitude)],
							[parseFloat(edge.to_panorama_node_longitude), parseFloat(edge.to_panorama_node_latitude)]
						]
					}
				}))
			};

			const buildingSource = minimap.current.getSource('source-building');
			if (buildingSource) {
				buildingSource.setData(buildingGeojson);
			}

			const nodesSource = minimap.current.getSource('source-nodes');
			if (nodesSource) {
				nodesSource.setData(nodesGeojson);
			}

			const edgesSource = minimap.current.getSource('source-edges');
			if (edgesSource) {
				edgesSource.setData(edgesGeojson);
			}

			const currentNodeSource = minimap.current.getSource('source-current-node');
			if (currentNodeSource) {
				currentNodeSource.setData({
					type: 'FeatureCollection',
					features: [{
						type: 'Feature',
						geometry: {
							type: 'Point',
							coordinates: [parseFloat(panorama_node.longitude), parseFloat(panorama_node.latitude)]
						},
						properties: {
							rotation: 180
						}
					}]
				});
			}

			// Center on user's current location
			minimap.current.setCenter([parseFloat(panorama_node.longitude), parseFloat(panorama_node.latitude)]);
			minimap.current.setZoom(20);
		};

		minimap.current.on('load', () => {
			// Ensure map is properly sized
			minimap.current.resize();

			// Add sources with empty data initially
			minimap.current.addSource('source-building', {
				type: 'geojson',
				data: { type: 'FeatureCollection', features: [] }
			});
			minimap.current.addSource('source-nodes', {
				type: 'geojson',
				data: { type: 'FeatureCollection', features: [] }
			});
			minimap.current.addSource('source-edges', {
				type: 'geojson',
				data: { type: 'FeatureCollection', features: [] }
			});
			minimap.current.addSource('source-current-node', {
				type: 'geojson',
				data: { type: 'FeatureCollection', features: [] }
			});

			// Add layers - building floor first (underneath everything)
			minimap.current.addLayer({
				id: 'layer-building-fill',
				type: 'fill',
				source: 'source-building',
				paint: {
					'fill-color': ['concat', '#', ['get', 'type_color_hex']],
					'fill-opacity': 1
				}
			});

			minimap.current.addLayer({
				id: 'layer-building-outline',
				type: 'line',
				source: 'source-building',
				paint: {
					'line-color': '#B4BFCB',
					'line-width': 2.5
				}
			});

			// Then edges
			minimap.current.addLayer({
				id: 'layer-edges',
				type: 'line',
				source: 'source-edges',
				paint: {
					'line-color': '#5340FF',
					'line-width': 2,
					'line-opacity': 1
				}
			});

			minimap.current.addLayer({
				id: 'layer-nodes',
				type: 'circle',
				source: 'source-nodes',
				paint: {
					'circle-radius': 6,
					'circle-color': '#7F94FF',
					'circle-stroke-width': 1,
					'circle-stroke-color': '#FFFFFF'
				}
			});

			// Add click handler for nodes
			minimap.current.on('click', 'layer-nodes', (e) => {
				if (e.features && e.features.length > 0) {
					const clickedNode = e.features[0];
					const nodeId = clickedNode.properties._id;

					if (nodeId && virtualTourPlugin.current) {
						virtualTourPlugin.current.setCurrentNode(nodeId);
					}
				}
			});

			// Change cursor on hover
			minimap.current.on('mouseenter', 'layer-nodes', () => {
				minimap.current.getCanvas().style.cursor = 'pointer';
			});

			minimap.current.on('mouseleave', 'layer-nodes', () => {
				minimap.current.getCanvas().style.cursor = '';
			});

			// Add current node with arrow icon
			const img = new Image();
			img.src = arrow;
			img.onload = () => {
				if (!minimap.current.hasImage('arrow_icon')) {
					minimap.current.addImage('arrow_icon', img);
				}

				minimap.current.addLayer({
					id: 'layer-current-node',
					type: 'symbol',
					source: 'source-current-node',
					layout: {
						'symbol-placement': 'point',
						'icon-offset': [0, 0],
						'icon-image': 'arrow_icon',
						'icon-rotate': ['get', 'rotation'],
						'icon-pitch-alignment': 'map',
						'icon-rotation-alignment': 'map',
						'icon-allow-overlap': true,
						'icon-size': 1.0
					}
				});

				// If data is already available, populate it now
				if (allNodesData && panorama_node) {
					populateMinimapData();
				}
			};
		});

		return () => {
			if (minimap.current) {
				minimap.current.remove();
				minimap.current = null;
			}
		};
	}, [allNodesData, isLoadingData, panorama_node, virtualTourPlugin]);

	// Update minimap data when allNodesData changes
	useEffect(() => {
		if (!minimap.current || !allNodesData || !panorama_node) return;

		const updateData = () => {
			if (!minimap.current.isStyleLoaded()) {
				return;
			}

			// Use building floor GeoJSON if available
			const buildingGeojson = allNodesData.geojson || {
				type: 'FeatureCollection',
				features: []
			};

			// Create GeoJSON for nodes
			const nodesGeojson = {
				type: 'FeatureCollection',
				features: allNodesData.nodes.map(node => ({
					type: 'Feature',
					geometry: {
						type: 'Point',
						coordinates: [parseFloat(node.longitude), parseFloat(node.latitude)]
					},
					properties: {
						_id: node._id,
						name: node.name
					}
				}))
			};

			// Create GeoJSON for edges
			const edgesGeojson = {
				type: 'FeatureCollection',
				features: allNodesData.edges.map(edge => ({
					type: 'Feature',
					geometry: {
						type: 'LineString',
						coordinates: [
							[parseFloat(edge.from_panorama_node_longitude), parseFloat(edge.from_panorama_node_latitude)],
							[parseFloat(edge.to_panorama_node_longitude), parseFloat(edge.to_panorama_node_latitude)]
						]
					}
				}))
			};

			// Update sources
			const buildingSource = minimap.current.getSource('source-building');
			if (buildingSource) {
				buildingSource.setData(buildingGeojson);
			}

			const nodesSource = minimap.current.getSource('source-nodes');
			if (nodesSource) {
				nodesSource.setData(nodesGeojson);
			}

			const edgesSource = minimap.current.getSource('source-edges');
			if (edgesSource) {
				edgesSource.setData(edgesGeojson);
			}

			const currentNodeSource = minimap.current.getSource('source-current-node');
			if (currentNodeSource) {
				currentNodeSource.setData({
					type: 'FeatureCollection',
					features: [{
						type: 'Feature',
						geometry: {
							type: 'Point',
							coordinates: [parseFloat(panorama_node.longitude), parseFloat(panorama_node.latitude)]
						},
						properties: {
							rotation: 180
						}
					}]
				});
			}
		};

		// Try to update immediately if style is loaded
		if (minimap.current.isStyleLoaded()) {
			updateData();
		} else {
			// Otherwise wait for the map to be ready
			const loadHandler = () => {
				updateData();
			};
			minimap.current.once('load', loadHandler);
			minimap.current.once('idle', loadHandler);
		}
	}, [allNodesData, panorama_node]);

	// Update minimap icon position and rotation - single unified effect
	useEffect(() => {
		if (!minimap.current || !allNodesData || !currentNodeId) return;

		const currentNode = allNodesData.nodesMap[currentNodeId];
		if (!currentNode) return;

		const newCoordinates = [parseFloat(currentNode.longitude), parseFloat(currentNode.latitude)];

		const source = minimap.current.getSource('source-current-node');
		if (!source) return;

		// With sphereCorrection (+180), viewerYaw needs +180 to get geographic direction
		const arrowRotation = (viewerYaw || 0) + 180;

		source.setData({
			type: 'FeatureCollection',
			features: [{
				type: 'Feature',
				geometry: {
					type: 'Point',
					coordinates: newCoordinates
				},
				properties: {
					rotation: arrowRotation
				}
			}]
		});

		// Pan map to new location (smooth animation)
		minimap.current.easeTo({
			center: newCoordinates,
			duration: 800,
			easing: (t) => t * (2 - t)
		});
	}, [currentNodeId, viewerYaw, allNodesData]);

	// Resize minimap when enlarging/shrinking
	useEffect(() => {
		if (!minimap.current) return;

		setTimeout(() => {
			minimap.current.resize();
		}, 300); // Wait for CSS transition
	}, [isMinimapEnlarged]);

	const handleToggleMinimapSize = () => {
		setIsMinimapEnlarged(!isMinimapEnlarged);
	};

	if (isLoadingData || !allNodesData) return null;

	return (
		<div style={{ position: 'relative' }}>
			<div
				ref={minimapContainer}
				className={`street-view-minimap ${isMinimapEnlarged ? 'enlarged' : ''}`}
			/>
			<button
				className="street-view-minimap-resize-btn"
				onClick={handleToggleMinimapSize}
				title={isMinimapEnlarged ? 'Shrink map' : 'Enlarge map'}
			>
				<FontAwesomeIcon icon={isMinimapEnlarged ? faCompress : faUpRightAndDownLeftFromCenter} />
			</button>
		</div>
	);
};

export default Minimap;
